import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CustomCursor from '@/components/CustomCursor';
import { Toaster } from 'sonner';
import {
  Shield, Cpu, Award, Briefcase, Code, Sparkles, GitBranch, Globe
} from 'lucide-react';

const TeamPage = () => {
  const trustChips = [
    { icon: Shield, label: 'SDVOSB Prime', sub: 'Verified by SBA' },
    { icon: Globe, label: 'Federal Track Record', sub: 'Since 2010' },
    { icon: Award, label: 'Patent Holder', sub: 'ACI #63/987,765' },
    { icon: Cpu, label: 'AI Native', sub: 'Built, not bolted on' }
  ];

  const coreyExpertise = [
    'Full-stack architecture',
    'AI engineering',
    'Cloud-native infrastructure',
    'Server & cybersecurity engineering',
    'Blockchain development',
    'Mobile development',
    'Product strategy'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-indigo-50">
      <CustomCursor />
      <Toaster position="top-center" />
      <Header />

      <main>
        {/* Hero */}
        <section className="relative pt-20 pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-100/50 via-transparent to-purple-100/50"></div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block px-4 py-1.5 bg-indigo-100 text-indigo-700 text-sm font-semibold rounded-full mb-6">
                The Team Behind AcuSightPro
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                A Federal Prime
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
                  Plus a Patented AI Team.
                </span>
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">
                AcuSightPro is built by Encore Services, LLC, an SDVOSB prime contracting partner since 2010, with a patented AI engineering team behind the Adaptive Compound Intelligence platform.
              </p>
            </div>

            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
              {trustChips.map((c) => (
                <div key={c.label} className="bg-white border border-gray-200 rounded-2xl p-5 text-center shadow-sm">
                  <c.icon className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                  <div className="text-sm font-semibold text-gray-900">{c.label}</div>
                  <div className="text-xs text-gray-500 mt-1">{c.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Dr. Jeff */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid md:grid-cols-[280px_1fr] gap-10 items-start max-w-5xl mx-auto">
              <div>
                <div className="bg-gradient-to-br from-purple-600/20 to-blue-600/10 rounded-3xl p-1 shadow-lg">
                  <div className="bg-white rounded-3xl p-2">
                    <div className="aspect-[3/4] rounded-2xl bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                      <Briefcase className="w-20 h-20 text-indigo-400" />
                    </div>
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <div className="text-xs uppercase tracking-widest text-purple-700 font-bold">Founder &amp; CEO</div>
                  <div className="text-xl font-bold text-gray-900 mt-1">Dr. Jeffrey A. Woodson</div>
                  <div className="text-sm text-gray-500 mt-1">Encore Services, LLC</div>
                </div>
              </div>

              <div>
                <div className="inline-block px-4 py-1.5 bg-purple-100 text-purple-700 text-sm font-semibold rounded-full mb-4">
                  Federal Technology Executive
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-5">
                  Dr. Jeff Woodson
                </h2>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Dr. Jeff Woodson is the Founder and CEO of Encore Services, LLC. Since founding the company in 2010, he has built it into a trusted federal IT partner delivering <strong className="text-gray-900">secure, scalable, and AI-driven technology solutions</strong>. His expertise spans IT infrastructure, program management, cybersecurity, software development, and digital transformation, with a focus on operational excellence, measurable outcomes, and client success.
                  </p>
                  <p>
                    Under his leadership, Encore has evolved into a respected partner to the Federal Government, providing comprehensive IT support and strategic consulting. Dr. Woodson is recognized for fostering a culture of integrity, collaboration, and continuous improvement. He resides in Bowie, Maryland, where he remains deeply committed to community service and charitable causes.
                  </p>
                </div>
                <blockquote className="mt-8 p-6 border-l-4 border-purple-600 italic text-lg text-gray-700 bg-purple-50 rounded-r-xl">
                  &ldquo;Our responsibility is to deliver for the mission, take care of our people, and earn our clients&rsquo; trust every single day.&rdquo;
                </blockquote>
                <div className="mt-6">
                  <a
                    href="https://www.encoresvcsllc.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-purple-700 hover:text-purple-900 transition-colors"
                  >
                    Visit Encore Services
                    <span>&rarr;</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Corey */}
        <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid md:grid-cols-[280px_1fr] gap-10 items-start max-w-5xl mx-auto">
              <div>
                <div className="bg-gradient-to-br from-blue-600/20 to-indigo-600/10 rounded-3xl p-1 shadow-lg">
                  <div className="bg-white rounded-3xl p-2">
                    <div className="aspect-[3/4] rounded-2xl bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                      <Code className="w-20 h-20 text-blue-400" />
                    </div>
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <div className="text-xs uppercase tracking-widest text-blue-700 font-bold">Fractional CTO</div>
                  <div className="text-xl font-bold text-gray-900 mt-1">Corey Strange</div>
                  <div className="text-sm text-gray-500 mt-1">Encore Services, LLC</div>
                </div>
              </div>

              <div>
                <div className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full mb-4">
                  Polymath Engineer &middot; AI Architect &middot; Inventor
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-5">
                  Corey Strange
                </h2>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Corey Strange is a polymath engineer with <strong className="text-gray-900">two decades of experience</strong> designing, building, and shipping complex production systems across web, mobile, AI, security, and infrastructure. As founder of Web Design Pros 365 and App Studio Pro, he has delivered <strong className="text-gray-900">300+ custom websites</strong>, <strong className="text-gray-900">150+ enterprise and security systems</strong>, and <strong className="text-gray-900">150+ cross-platform applications</strong> across iOS, Android, and web.
                  </p>
                  <p>
                    His cross-composite skill set spans full-stack architecture, AI engineering, cloud-native infrastructure, server and cybersecurity engineering, blockchain development, mobile development, and product strategy. He holds <strong className="text-gray-900">multiple utility patents</strong> in adaptive AI systems and previously founded <strong className="text-gray-900">Rocket PC</strong>, a water-cooled performance PC company acquired by Alienware.
                  </p>
                  <p>
                    As Encore&rsquo;s Fractional CTO, Corey leads AI platform architecture, technical strategy, and engineering execution behind AcuSightPro and the Adaptive Compound Intelligence platform.
                  </p>
                </div>

                <div className="mt-6">
                  <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-3">Cross-composite stack</div>
                  <div className="flex flex-wrap gap-2">
                    {coreyExpertise.map((e) => (
                      <span key={e} className="inline-flex items-center px-3 py-1 bg-blue-50 border border-blue-200 text-blue-800 text-xs font-medium rounded-full">{e}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Built by Encore */}
        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-10">
              <span className="inline-block px-4 py-1.5 bg-emerald-100 text-emerald-700 text-sm font-semibold rounded-full mb-4">
                Built By
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Encore Services, LLC
              </h2>
              <p className="text-lg text-gray-600">
                A federal prime contracting partner with two decades of trust, building the next generation of acquisition intelligence in-house.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-purple-50 to-white border border-purple-200 rounded-2xl p-7">
                <Shield className="w-8 h-8 text-purple-700 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">SDVOSB Prime</h3>
                <p className="text-gray-700 leading-relaxed text-sm mb-3">
                  SDVOSB prime contractor headquartered in Largo, MD. Federal track record since 2010 across IT infrastructure, cybersecurity, software development, program management, and digital transformation.
                </p>
                <p className="text-gray-600 text-sm">
                  Encore brings the contract vehicles, the federal compliance muscle, and the prime sponsorship that makes production deployments possible.
                </p>
                <a
                  href="https://www.encoresvcsllc.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-purple-700 hover:text-purple-900"
                >
                  encoresvcsllc.com &rarr;
                </a>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-2xl p-7">
                <Sparkles className="w-8 h-8 text-blue-700 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Patented AI Platform</h3>
                <p className="text-gray-700 leading-relaxed text-sm mb-3">
                  AcuSightPro is powered by patented Adaptive Compound Intelligence (US Patent Pending #63/987,765), the ACUMEN-10 engine, and a compound learning architecture purpose-built for federal acquisition.
                </p>
                <p className="text-gray-600 text-sm">
                  Deep AI engineering depth, an active patent portfolio, and long-term R&amp;D commitment that turns acquisition workflows into adaptive intelligence.
                </p>
                <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-blue-700">
                  <GitBranch className="w-4 h-4" /> Patent-protected platform
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-r from-purple-600 via-indigo-700 to-blue-700 text-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-4">Want to Meet the Team?</h2>
            <p className="text-lg text-indigo-100 mb-8 max-w-2xl mx-auto">
              We&rsquo;re happy to walk acquisition leadership, technical staff, and program officers through the platform, the patent, and the federal integration plan.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact" className="px-8 py-3 bg-white text-indigo-700 font-semibold rounded-full shadow-lg hover:opacity-90 transition-all">
                Schedule a Walkthrough
              </a>
              <a href="/financial-integration" className="px-8 py-3 border-2 border-white text-white font-semibold rounded-full hover:bg-white hover:text-indigo-700 transition-all">
                See Financial Integration
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default TeamPage;
